import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { MapPin, CheckCircle, MessageCircle, Truck, Package, ArrowRight, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';
import { CONTACT_INFO, SERVICE_TIERS, TRUST_BADGES } from '@/data/mockData';
import areaConfig, { generateAreaFAQs } from '@/data/areaConfig';

const AreaPage = () => {
  const { slug } = useParams();
  const area = areaConfig[slug];

  // Redirect to main areas page if area not found
  if (!area) {
    return <Navigate to="/areas" replace />;
  }

  const whatsappLink = generateWhatsAppLink('generic', { 
    suburb: area.name,
    issue: `I need a quote for ${area.name}`
  });

  const faqs = generateAreaFAQs(area.name);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <MapPin className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Mini Moves & Deliveries in {area.name}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Stellenbosch & Cape Town | Fast • Reliable
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8 py-6 text-lg">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp for Quote
                </Button>
              </a>
              <a href={`/quote?suburb=${area.name}`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47] px-8 py-6 text-lg">
                  Get a Quote
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {TRUST_BADGES.slice(0, 4).map((badge, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <CheckCircle className="h-6 w-6 text-[#039fff] mb-2" />
                <p className="text-sm text-gray-700">{badge.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Summary */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] mb-6 text-center">
              Fast & Reliable Service in {area.name}
            </h2>
            <p className="text-lg text-gray-600 mb-8 text-center">
              {area.description}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <Truck className="h-10 w-10 text-[#039fff] mx-auto mb-3" />
                  <h3 className="font-bold text-[#001A47] mb-2">What We Move</h3>
                  <p className="text-sm text-gray-600">Furniture, appliances, boxes, marketplace items</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-10 w-10 text-[#fb6402] mx-auto mb-3" />
                  <h3 className="font-bold text-[#001A47] mb-2">Service Options</h3>
                  <p className="text-sm text-gray-600">Same-day, next-day, or scheduled moves</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <MapPin className="h-10 w-10 text-[#039fff] mx-auto mb-3" />
                  <h3 className="font-bold text-[#001A47] mb-2">Area Connections</h3>
                  <p className="text-sm text-gray-600">{area.name} to anywhere in Western Cape</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Specialties */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] mb-8 text-center">
              {area.name} Specialties
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {area.specialties.map((specialty, index) => (
                <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-[#039fff] flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{specialty}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] mb-8 text-center">Our Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Package className="h-10 w-10 text-[#039fff] mb-4" />
                  <h3 className="text-xl font-bold text-[#001A47] mb-3">Deliveries</h3>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                      Store pickups & marketplace items
                    </li>
                    <li className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                      Same-day or scheduled delivery
                    </li>
                    <li className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                      Real-time WhatsApp updates
                    </li>
                  </ul>
                  <a href={`/deliveries?suburb=${area.name}`}>
                    <Button className="w-full bg-[#001A47] hover:bg-[#002B6B]">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Truck className="h-10 w-10 text-[#039fff] mb-4" />
                  <h3 className="text-xl font-bold text-[#001A47] mb-3">Mini-Moves</h3>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                      Furniture & appliance moves
                    </li>
                    <li className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                      Helper options available
                    </li>
                    <li className="flex items-start text-sm">
                      <CheckCircle className="h-4 w-4 text-[#fb6402] mr-2 flex-shrink-0 mt-0.5" />
                      Care-first handling with straps
                    </li>
                  </ul>
                  <a href={`/mini-moves?suburb=${area.name}`}>
                    <Button className="w-full bg-[#001A47] hover:bg-[#002B6B]">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Tiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] mb-8 text-center">Choose Your Speed</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {SERVICE_TIERS.map((tier) => (
                <Card key={tier.id} className="border-2 hover:border-[#039fff] transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="text-2xl font-bold text-[#fb6402] mb-2">{tier.name}</div>
                    <p className="text-sm text-gray-600 mb-2">{tier.description}</p>
                    <p className="text-xs text-[#039fff] font-semibold">{tier.tagline}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Access Notes */}
      {area.accessNotes && (
        <section className="py-12 bg-[#039fff]/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-[#001A47] mb-3 flex items-center">
                    <MapPin className="h-5 w-5 text-[#039fff] mr-2" />
                    {area.name} Access Information
                  </h3>
                  <p className="text-sm text-gray-700">{area.accessNotes}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Quote Block */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">
              Ready to Move in {area.name}?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Get your quote in minutes via WhatsApp or our online form
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp for Quote
                </Button>
              </a>
              <a href={`/quote?suburb=${area.name}`}>
                <Button size="lg" variant="outline" className="px-8">
                  Use Quote Form
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] mb-8 text-center">
              Frequently Asked Questions - {area.name}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-[#001A47] hover:text-[#039fff]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] mb-8 text-center">
              Nearby Areas We Serve
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {area.nearby.map((nearbyArea, index) => {
                const nearbySlug = nearbyArea.toLowerCase().replace(/ /g, '-');
                const nearbyExists = areaConfig[nearbySlug];
                
                return nearbyExists ? (
                  <a key={index} href={`/areas/${nearbySlug}`}>
                    <Button variant="outline" className="hover:bg-[#039fff] hover:text-white">
                      {nearbyArea}
                    </Button>
                  </a>
                ) : (
                  <Button key={index} variant="outline" disabled>
                    {nearbyArea}
                  </Button>
                );
              })}
              <a href="/areas">
                <Button variant="outline" className="border-[#fb6402] text-[#fb6402] hover:bg-[#fb6402] hover:text-white">
                  View All Areas
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help? We're Here</h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact us for immediate assistance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02]">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
            <a href={`tel:${CONTACT_INFO.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47]">
                <Phone className="mr-2 h-5 w-5" />
                Call {CONTACT_INFO.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AreaPage;
